import React, { useContext, useState } from 'react'
import Promotions, { OrderType } from 'components/promotions'
import { AdjustmentTypeEnumType } from 'enums'
import {
  useOrderAndFpMemberCouponsQuery,
  useApplyPromoToOrderMutation,
  useRemoveOrderAdjustmentMutation,
  useInitiateCheckoutMutation,
  useOrderAndHhMemberCouponsQuery,
} from 'graphQL/dinein/generate/operations'
import ModalWarningTotalZeroDue from 'components/modals/ModalWarningTotalZeroDue'
import { useHistory } from 'react-router-dom'
import { SubPageName } from 'enums/buffet'
import { useSelector } from 'react-redux'
import { buffetSelector } from 'store/slices/buffetSlice'
import { dineInClient } from 'variables/services'
import { sendAnalyticsDineInSelectPromotion } from 'helpers/analytics'
import {
  findTypeOfPromoCodeFromAdjustment,
  findTypeOfExternalCouponFromAdjustment,
  validateAdjusments,
} from 'helpers/promotion'
import useValidateCurrentOrder, { OrderInvalidEnum } from 'hooks/buffet/useValidateCurrentOrder'
import useHandleBuffetOrder from 'hooks/buffet/useHandleBuffetOrder'
import { ModalEnum } from 'components/buffet/BuffetModals'
import { ScreenEnum } from 'components/buffet/BuffetOverlayPage'
import { AppContext } from 'contexts/AppProvider'
import { THEMES_UI } from 'brands'
import usePromotion from 'hooks/dine-in/usePromotion'
import { useHandleNetworkErrorMutation } from 'hooks/useHandleError'
import useCombineCoupon from 'hooks/buffet/useCombineCoupon'
import useLoadingPage from 'hooks/useLoadingPage'
import { useTranslation } from 'react-i18next'
import { MAXIMUM_SHARE_COUPONS } from 'variables/dineIn'
import ModalLimitSharingCoupons from 'components/dine-in/modal/ModalLimitSharingCoupons'
import ModalConfirmCombineCoupon from 'components/dine-in/modal/ModalConfirmCombineCoupon'
import LoadingBrand from 'components/LoadingBrand'

const PromotionController = () => {
  const buffet = useSelector(buffetSelector)
  const { t } = useTranslation()
  const history = useHistory()
  const { themeUI } = useContext(AppContext)
  const [activeTab, setActiveTab] = useState<string>(AdjustmentTypeEnumType.Discount)
  const { isLoadingPage, setIsLoadingPage } = useLoadingPage({ initialIsLoadingPage: true })
  const [isCashVoucherErrorZerodue, setIsCashVoucherErrorZerodue] = useState(false)
  const [applyPromoToOrder] = useApplyPromoToOrderMutation({ client: dineInClient })
  const [removeOrderAdjustment] = useRemoveOrderAdjustmentMutation({ client: dineInClient })
  const handleNetWorkErrorMutation = useHandleNetworkErrorMutation()
  const { handleValidateResult } = useHandleBuffetOrder({
    skipModal: [ModalEnum.TimeUp, ModalEnum.ReadyToCheckout, ModalEnum.ContinueCheckout],
    skipScreen: [ScreenEnum.CreditCardFailed, ScreenEnum.ResubmitPayment],
  })
  const { validateCurrentOrder } = useValidateCurrentOrder()
  const [initiateCheckout] = useInitiateCheckoutMutation({
    client: dineInClient,
  })
  const { data: orderAndFpMemberCouponsResult, refetch: refetchOrderAndFpMemberCoupons } =
    useOrderAndFpMemberCouponsQuery({
      client: dineInClient,
      fetchPolicy: 'no-cache',
      skip: themeUI !== THEMES_UI.GON,
      variables: { orderID: buffet.table.orderID },
      onCompleted: async (result) => {
        setIsLoadingPage(false)
        const validateResult = await customValidateOrder()
        if (
          validateResult.invalid === OrderInvalidEnum.ReadyToCheckout &&
          result.orderAndFPMemberCoupons.order.userID === 0
        ) {
          await initiateCheckout({
            variables: {
              orderID: buffet.table.orderID,
              override: true,
            },
          })
          await refetchOrderAndFpMemberCoupons()
        }
      },
    })

  const { data: orderAndHhMemberCouponsResult, refetch: refetchOrderAndHhMemberCoupons } =
    useOrderAndHhMemberCouponsQuery({
      client: dineInClient,
      fetchPolicy: 'no-cache',
      skip: themeUI === THEMES_UI.GON,
      variables: { orderID: buffet.table.orderID },
      onCompleted: async (result) => {
        setIsLoadingPage(false)
        const validateResult = await customValidateOrder()
        if (
          validateResult.invalid === OrderInvalidEnum.ReadyToCheckout &&
          result.orderAndHHMemberCoupons.order.userID === 0
        ) {
          await initiateCheckout({
            variables: {
              orderID: buffet.table.orderID,
              override: true,
            },
          })
          await refetchOrderAndFpMemberCoupons()
        }
      },
    })

  const {
    orderAndMemberCoupons,
    memberCouponsFP,
    autoPromotions,
    memberPromotions,
    generalPromotions,
    memberCouponsHH,
  } = usePromotion({ orderAndFpMemberCouponsResult, orderAndHhMemberCouponsResult })
  const { handleClickCombineCoupon, confirmCombineCoupon, modalLimitSharingCoupons, modalCombineCoupons } =
    useCombineCoupon()

  const customValidateOrder = () => {
    const validateResult = validateCurrentOrder(buffet.currentOrder)
    if (
      validateResult.isOK ||
      validateResult.invalid === OrderInvalidEnum.OwnerNeedToCheckout ||
      validateResult.invalid === OrderInvalidEnum.CreditCardFailed ||
      validateResult.invalid === OrderInvalidEnum.PaymentIsNotCompleted
    ) {
      return { ...validateResult, isOK: true }
    } else {
      handleValidateResult(validateResult, buffet.currentOrder)
      return validateResult
    }
  }

  const onApplyPromoCode = async (
    promoCode: string | undefined,
    externalMemberCouponRefID: string | undefined,
    hatoHeartMemberCouponID: number | undefined
  ) => {
    try {
      const result = await applyPromoToOrder({
        variables: {
          orderID: buffet.table.orderID,
          promoCode: promoCode,
          externalMemberCouponRefID: externalMemberCouponRefID,
          hatoHeartMemberCouponID: hatoHeartMemberCouponID,
        },
      })
      return new Promise(async (resolve: (data: string) => void, reject) => {
        let rewardType = AdjustmentTypeEnumType.Discount
        const promotionInvalids = result.data?.applyPromoToOrder?.promotionInvalids ?? []

        if (promotionInvalids.length === 0) {
          const order = result.data?.applyPromoToOrder.order
          if (order) {
            const validate = validateAdjusments(order.adjustments)
            setIsCashVoucherErrorZerodue(!validate)
          }
          let promotionID
          if (promoCode) {
            promotionID = promoCode
            rewardType = findTypeOfPromoCodeFromAdjustment(order?.adjustments ?? [], promoCode)
          } else if (externalMemberCouponRefID) {
            promotionID = externalMemberCouponRefID
            rewardType = findTypeOfExternalCouponFromAdjustment(order?.adjustments ?? [], externalMemberCouponRefID)
          } else {
            promotionID = hatoHeartMemberCouponID
          }
          sendAnalyticsDineInSelectPromotion({ promotionID })
          resolve(rewardType)
        } else {
          reject([promotionInvalids])
        }
      })
    } catch (error) {
      handleNetWorkErrorMutation(error)
    }
    return new Promise((resolve: (data: string) => void, reject) => {
      resolve('')
    })
  }
  const onRemoveCartAdjustment = async (
    externalCouponID: number,
    promotionID: number,
    hatoHeartMemberCouponID: number,
    cartAndMemberCoupons: OrderType
  ) => {
    try {
      const result = await removeOrderAdjustment({
        variables: {
          orderID: buffet.table.orderID,
          promotionID: promotionID,
          externalCouponID: externalCouponID,
          hatoHeartMemberCouponID,
        },
      })
      onRefetch()
      return new Promise((resolve: (data: string) => void, reject) => {
        const promotionInvalids = result?.data?.removeOrderAdjustment.promotionInvalids ?? []
        if (promotionInvalids.length === 0) {
          resolve('Removed order adjustment completed')
        } else {
          reject(promotionInvalids)
        }
      })
    } catch (error) {
      console.log(error)
    }

    return new Promise((resolve: (data: string) => void, reject) => {
      resolve('')
    })
  }

  const onRefetch = async () =>
    themeUI === THEMES_UI.GON ? await refetchOrderAndFpMemberCoupons() : await refetchOrderAndHhMemberCoupons()

  return (
    <div>
      <Promotions
        onApplyPromoCode={onApplyPromoCode}
        onRemoveCartAdjustment={onRemoveCartAdjustment}
        isLoadingPage={isLoadingPage}
        activeTab={activeTab}
        orderAndMemberCoupons={orderAndMemberCoupons}
        memberCouponsFP={memberCouponsFP}
        memberCouponsHH={memberCouponsHH}
        autoPromotions={autoPromotions}
        memberPromotions={memberPromotions}
        generalPromotions={generalPromotions}
        onRefetch={onRefetch}
        onClickCombineCoupon={handleClickCombineCoupon}
        onClickRefreshCoupon={async () => {
          setIsLoadingPage(true)
          await onRefetch()
          setIsLoadingPage(false)
        }}
        handleBack={() => history.push(SubPageName.Checkout)}
        allowedSharingCoupon={buffet.allowedSharingCoupon}
      />

      <ModalWarningTotalZeroDue
        isOpen={isCashVoucherErrorZerodue}
        onClose={() => setIsCashVoucherErrorZerodue(false)}
        onClickButton={() => {
          setIsCashVoucherErrorZerodue(false)
          setTimeout(() => setActiveTab(''))
          setTimeout(() => setActiveTab(AdjustmentTypeEnumType.Cash))
        }}
      />

      <ModalLimitSharingCoupons
        total={MAXIMUM_SHARE_COUPONS}
        isOpen={modalLimitSharingCoupons.open}
        onRequestClose={() => modalLimitSharingCoupons.setOpen(false)}
        btnAccept={{
          content: t('dineIn.modal.limitSharingCoupons.accept'),
          onClick: () => modalLimitSharingCoupons.setOpen(false),
        }}
      />

      <ModalConfirmCombineCoupon
        isOpen={modalCombineCoupons.open}
        onRequestClose={() => {
          modalCombineCoupons.setOpen(false)
        }}
        btnAccept={{
          content: t('dineIn.modal.shareCoupon.confirm'),
          isProcessing: modalCombineCoupons.processing,
          onClick: confirmCombineCoupon,
        }}
        btnCancel={{
          content: t('dineIn.modal.shareCoupon.cancel'),
          outline: true,
          onClick: () => modalCombineCoupons.setOpen(false),
        }}
      />
      <LoadingBrand show={isLoadingPage} />
    </div>
  )
}

export default PromotionController
